import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQuery } from '@apollo/client';
import Select from 'react-select';
import { Button } from 'reactstrap';
import MaptilerMap from './MaptilerMap';
import { CITIES, NEIGHBORHOODS, NEIGHBORHOODS_GEOJSON, ZIP_CODES, PROPERTIES_GEOJSON } from '@api';
import '../../../assets/styles/LocationAreaOverlay.css';

const DEFAULT_DRAW_TEXT = 'No custom areas drawn';
const DEFAULT_RADIUS_TEXT =
  'Step 1: Click anywhere on the map to set search center';

const LocationAreaOverlay = ({
  isOpen,
  onClose,
  getFilter,
  addFilter,
  changeFilters,
  setShouldSearch,
  setShouldMapSearch,
}) => {
  const rawCityFilter = getFilter('city');

  const selectedCityOption = useMemo(() => {
    if (!rawCityFilter || rawCityFilter === '{}') {
      return null;
    }

    try {
      return JSON.parse(rawCityFilter);
    } catch (error) {
      return null;
    }
  }, [rawCityFilter]);

  const selectedCityId = selectedCityOption?.value
    ? Number(selectedCityOption.value)
    : null;
  const selectedCityName = selectedCityOption?.label || '';

  const selectedNeighborhoods = getFilter('neighborhoods') || [];
  const selectedZipCodes = getFilter('zipCodes') || [];

  const selectedZipValues = useMemo(() => {
    return selectedZipCodes
      ? selectedZipCodes.map((z) => (z?.value !== undefined ? String(z.value) : String(z)))
      : [];
  }, [selectedZipCodes]);

  const citiesQuery = useQuery(CITIES);
  const { data: citiesData } = citiesQuery;

  const [showNeighborhoods, setShowNeighborhoods] = useState(false);
  const [showApartments, setShowApartments] = useState(true);
  const [showSchools, setShowSchools] = useState(false);
  const [showDistricts, setShowDistricts] = useState(false);
  const [onlySelectedTop, setOnlySelectedTop] = useState(false);

  const [drawMode, setDrawMode] = useState(false);
  const [drawPanelOpen, setDrawPanelOpen] = useState(false);
  const [drawSummary, setDrawSummary] = useState({
    text: DEFAULT_DRAW_TEXT,
    polygons: 0,
    apartments: 0,
  });
  const [clearDrawKey, setClearDrawKey] = useState(0);

  const [radiusMode, setRadiusMode] = useState(false);
  const [radiusPanelOpen, setRadiusPanelOpen] = useState(false);
  const [radiusActive, setRadiusActive] = useState(false);
  const [radiusMiles, setRadiusMiles] = useState(5);
  const [radiusCenter, setRadiusCenter] = useState(null);
  const [radiusSummary, setRadiusSummary] = useState({
    text: DEFAULT_RADIUS_TEXT,
    apartments: 0,
    ready: false,
  });

  const [apartmentStats, setApartmentStats] = useState({
    totalApartments: 0,
    apartmentsInSelection: 0,
    apartmentsInCity: 0,
    apartmentsVisible: 0,
    drawnPolygonCount: 0,
    radiusActive: false,
    selectedNeighborhoodCount: 0,
    propertyIds: [],
  });

  useEffect(() => {
    if (!citiesData?.cities?.length) {
      return;
    }

    if (rawCityFilter && rawCityFilter !== '{}') {
      return;
    }

    const defaultCityId = localStorage.getItem('default_city_id');

    const fallbackCity = defaultCityId
      ? citiesData.cities.find(
        (city) => String(city.id) === String(defaultCityId)
      ) || citiesData.cities[0]
      : citiesData.cities[0];

    if (fallbackCity) {
      const serializedCity = JSON.stringify({
        value: String(fallbackCity.id),
        label: fallbackCity.name,
      });

      addFilter({ e: serializedCity, key: 'city' });
    }
  }, [citiesData, rawCityFilter, addFilter]);

  const neighborhoodQuery = useQuery(NEIGHBORHOODS, {
    variables: { cityId: selectedCityId },
    skip: !selectedCityId,
  });

  const zipCodesQuery = useQuery(ZIP_CODES, {
    variables: { cityId: selectedCityId },
    skip: !selectedCityId,
  });

  const neighborhoodGeojsonQuery = useQuery(NEIGHBORHOODS_GEOJSON, {
    variables: { cityId: selectedCityId },
    skip: !selectedCityId,
  });

  const { data: propertiesData, loading: propertiesLoading } = useQuery(PROPERTIES_GEOJSON, {
    variables: {
      cityId: selectedCityId,
      search: getFilter('search') || null,
      minRent: getFilter('minPrice') ? parseInt(getFilter('minPrice')) : null,
      maxRent: getFilter('maxPrice') ? parseInt(getFilter('maxPrice')) : null,
      availFrom: getFilter('availFrom') || null,
      availTo: getFilter('availTo') || null,
      yearFrom: getFilter('yearFrom') || null,
      yearTo: getFilter('yearTo') || null,
      sqFeet: getFilter('sqFt') || null,
      escort: getFilter('escort') ? getFilter('escort') : null,
      sendEscort: getFilter('minSend') ? getFilter('minSend') : null,
      zip: getFilter('zip') || null,
      bedroom: getFilter('bed') && getFilter('bed').length > 0 ? getFilter('bed') : null,
      bathroom: getFilter('bath') && JSON.parse(getFilter('bath'))?.label ? [JSON.parse(getFilter('bath')).label] : null,
      neighborhoodIds: selectedNeighborhoods.length > 0 ? selectedNeighborhoods.map(n => Number(n.value)) : null,
      zipCodes: selectedZipCodes.length > 0 ? selectedZipCodes.map(z => z.value) : null,
      policies: getFilter('policies') && getFilter('policies').length > 0 ? getFilter('policies').map(p => p.label) : null,
      unitFeatures: getFilter('unitFeatures') && getFilter('unitFeatures').length > 0 ? getFilter('unitFeatures').map(f => f.label) : null,
      amenities: getFilter('amenities') && getFilter('amenities').length > 0 ? getFilter('amenities').map(a => a.label) : null,
      onlyAvailable: true,
    },
    skip: !selectedCityId,
    fetchPolicy: 'cache-and-network',
  });

  const neighborhoodOptions = useMemo(() => {
    if (!neighborhoodQuery.data?.neighborhoods?.length) {
      return [];
    }

    return neighborhoodQuery.data.neighborhoods.map((neighborhood) => ({
      value: String(neighborhood.id),
      label: neighborhood.name,
    }));
  }, [neighborhoodQuery.data]);

  const zipCodeOptions = useMemo(() => {
    if (!zipCodesQuery.data?.zipCodes?.length) {
      return [];
    }

    return zipCodesQuery.data.zipCodes.map((zipCode) => ({
      value: String(zipCode),
      label: String(zipCode),
    }));
  }, [zipCodesQuery.data]);

  const handleCitySelect = (city) => {
    const serializedCity = JSON.stringify({
      value: String(city.id),
      label: city.name,
    });

    addFilter({ e: serializedCity, key: 'city' });
    addFilter({ e: [], key: 'neighborhoods' });
    addFilter({ e: [], key: 'zipCodes' });

    if (city.name === 'Denver' || city.name === 'Nashville') {
      setShowNeighborhoods(false);
    }
  };

  const handleNeighborhoodChange = (options) => {
    addFilter({ e: options || [], key: 'neighborhoods' });
  };

  const handleZipCodeChange = (options) => {
    addFilter({ e: options || [], key: 'zipCodes' });
  };

  const handleToggleNeighborhood = (neighborhoodOption) => {
    const exists = selectedNeighborhoods.some(
      (option) => option.value === String(neighborhoodOption.value)
    );

    const updated = exists
      ? selectedNeighborhoods.filter(
        (option) => option.value !== String(neighborhoodOption.value)
      )
      : [...selectedNeighborhoods, neighborhoodOption];

    addFilter({ e: updated, key: 'neighborhoods' });
  };

  const handleDrawUpdate = useCallback(
    ({ polygons = [], apartmentsInPolygons = 0 }) => {
      const polygonCount = polygons.length;
      setDrawSummary({
        text:
          polygonCount > 0
            ? `📐 ${apartmentsInPolygons} apartments fall within ${polygonCount} custom area${polygonCount > 1 ? 's' : ''}.`
            : DEFAULT_DRAW_TEXT,
        polygons: polygonCount,
        apartments: apartmentsInPolygons,
      });
    },
    []
  );

  const handleRadiusUpdate = useCallback(
    ({
      status,
      center,
      radiusMiles: miles,
      apartmentsInRadius = 0,
      applied,
    }) => {
      if (status === 'cleared') {
        setRadiusCenter(null);
        setRadiusActive(false);
        setRadiusSummary({
          text: DEFAULT_RADIUS_TEXT,
          apartments: 0,
          ready: false,
        });
        return;
      }

      if (center) {
        setRadiusCenter(center);
      }

      const effectiveMiles =
        typeof miles === 'number' ? miles : radiusMiles;
      const message = center
        ? applied
          ? `✅ Filter applied: ${apartmentsInRadius} apartments within ${effectiveMiles.toFixed(
            1
          )} miles.`
          : `🎯 ${apartmentsInRadius} apartments within ${effectiveMiles.toFixed(
            1
          )} miles.`
        : DEFAULT_RADIUS_TEXT;

      setRadiusSummary({
        text: message,
        apartments: apartmentsInRadius,
        ready: Boolean(center),
      });

      if (typeof applied === 'boolean') {
        setRadiusActive(applied);
      }
    },
    [radiusMiles]
  );

  const handleStatsUpdate = useCallback((stats) => {
    if (!stats) {
      return;
    }
    setApartmentStats(stats);
  }, []);

  const handleClear = () => {
    changeFilters({
      type: 'reset',
      name: ['neighborhoods', 'zipCodes', 'propertyIds'],
    });
    setShouldSearch(true);
    setShouldMapSearch(true);
    setDrawMode(false);
    setRadiusMode(false);
    setOnlySelectedTop(false);
    if (drawSummary.polygons > 0) {
      setClearDrawKey((key) => key + 1);
    }
    setDrawSummary({
      text: DEFAULT_DRAW_TEXT,
      polygons: 0,
      apartments: 0,
    });
    setRadiusActive(false);
    setRadiusCenter(null);
    setRadiusSummary({
      text: DEFAULT_RADIUS_TEXT,
      apartments: 0,
      ready: false,
    });
  };

  const handleApply = () => {
    const ids = apartmentStats.propertyIds?.length > 0 ? apartmentStats.propertyIds.map(id => Number(id)) : null;
    addFilter({ e: ids, key: 'propertyIds' });
    setShouldSearch(true);
    setShouldMapSearch(true);
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  const handleDrawButton = () => {
    if (!drawPanelOpen) {
      setDrawPanelOpen(true);
    }
    const next = !drawMode;
    setDrawMode(next);
    if (next) {
      setRadiusMode(false);
      setRadiusPanelOpen(false);
    }
  };

  const handleDrawPanelClose = () => {
    setDrawMode(false);
    setDrawPanelOpen(false);
  };

  const handleDrawCancel = () => {
    setDrawMode(false);
  };

  const handleDrawClearAll = () => {
    setClearDrawKey((key) => key + 1);
    setDrawSummary({
      text: DEFAULT_DRAW_TEXT,
      polygons: 0,
      apartments: 0,
    });
    setDrawMode(false);
  };

  const handleRadiusButton = () => {
    if (!radiusPanelOpen) {
      setRadiusPanelOpen(true);
    }
    const next = !radiusMode;
    setRadiusMode(next);
    if (next) {
      setDrawMode(false);
      setDrawPanelOpen(false);
      setRadiusActive(false);
      setRadiusCenter(null);
      setRadiusSummary({
        text: DEFAULT_RADIUS_TEXT,
        apartments: 0,
        ready: false,
      });
    }
  };

  const handleRadiusClose = () => {
    setRadiusMode(false);
    setRadiusPanelOpen(false);
  };

  const handleRadiusCancel = () => {
    setRadiusMode(false);
    setRadiusActive(false);
    setRadiusCenter(null);
    setRadiusSummary({
      text: DEFAULT_RADIUS_TEXT,
      apartments: 0,
      ready: false,
    });
  };

  const handleRadiusApply = () => {
    if (!radiusSummary.ready || !radiusCenter) {
      return;
    }
    setRadiusActive(true);
    setRadiusMode(false);
    setRadiusPanelOpen(false);
    setRadiusSummary((prev) => ({
      ...prev,
      text: `✅ Filter applied: ${prev.apartments} apartments within ${radiusMiles.toFixed(
        1
      )} miles.`,
    }));
  };

  const handleRadiusSliderChange = (event) => {
    const value = Number(event.target.value);
    if (!Number.isNaN(value)) {
      setRadiusMiles(value);
    }
  };

  const hasSpatialSelection =
    selectedNeighborhoods.length > 0 ||
    drawSummary.polygons > 0 ||
    (radiusActive && radiusCenter);

  useEffect(() => {
    if (!hasSpatialSelection && onlySelectedTop) {
      setOnlySelectedTop(false);
    }
  }, [hasSpatialSelection, onlySelectedTop]);

  useEffect(() => {
    if (!isOpen) {
      setDrawMode(false);
      setRadiusMode(false);
      setDrawPanelOpen(false);
      setRadiusPanelOpen(false);
      // Remove scroll lock when overlay closes
      document.documentElement.classList.remove('location-overlay-open');
      document.body.classList.remove('location-overlay-open');
    } else {
      // Add scroll lock when overlay opens
      document.documentElement.classList.add('location-overlay-open');
      document.body.classList.add('location-overlay-open');
    }

    // Cleanup on unmount
    return () => {
      document.documentElement.classList.remove('location-overlay-open');
      document.body.classList.remove('location-overlay-open');
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className='location-area-overlay'>
      <div
        className='location-area-overlay__backdrop'
      />

      <div className='location-area-overlay__content'>
        {/* <header className='location-area-overlay__header'>
          <div>
            <h2 className='location-area-overlay__title'>Market Explorer</h2>
          </div>

          <div className='location-area-overlay__header-actions'>
          </div>
        </header> */}

        <div className='location-area-overlay__body'>
          <div className='location-area-overlay__controls'>
            <div className='location-area-overlay__filters-grid'>
              <section className='location-area-overlay__section'>
                <div className='location-area-overlay__section-header'>
                  <div>
                    <h3>Market</h3>
                  </div>
                  <span className='location-area-overlay__count'>
                  </span>
                </div>
                <Select
                  placeholder="Select a market"
                  options={citiesData?.cities?.map((city) => ({
                    value: String(city.id),
                    label: city.name
                  }))}
                  value={selectedCityOption}
                  onChange={(option) => handleCitySelect({
                    id: option.value,
                    name: option.label
                  })}
                  className='location-area-overlay__select'
                  classNamePrefix='spark-select'
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                  menuPortalTarget={document.body}
                />
              </section>

              <section className='location-area-overlay__section'>
                <div className='location-area-overlay__section-header'>
                  <div>
                    <h3>Neighborhoods</h3>
                  </div>
                  <span className='location-area-overlay__count'>
                    {selectedNeighborhoods.length} selected
                  </span>
                </div>

                <Select
                  isMulti
                  isDisabled={!selectedCityId}
                  placeholder={
                    selectedCityId ? 'Search neighborhoods' : 'Choose a market first'
                  }
                  options={neighborhoodOptions}
                  value={selectedNeighborhoods}
                  onChange={handleNeighborhoodChange}
                  className='location-area-overlay__select'
                  classNamePrefix='spark-select'
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                  menuPortalTarget={document.body}
                  closeMenuOnSelect={false}
                />
              </section>

              <section className='location-area-overlay__section'>
                <div className='location-area-overlay__section-header'>
                  <div>
                    <h3>ZIP Codes</h3>
                  </div>
                  <span className='location-area-overlay__count'>
                    {selectedZipCodes.length} selected
                  </span>
                </div>

                <Select
                  isMulti
                  isDisabled={!selectedCityId}
                  placeholder={
                    selectedCityId ? 'Search ZIP codes' : 'Choose a market first'
                  }
                  options={zipCodeOptions}
                  value={selectedZipCodes}
                  onChange={handleZipCodeChange}
                  className='location-area-overlay__select'
                  classNamePrefix='spark-select'
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                  menuPortalTarget={document.body}
                  closeMenuOnSelect={false}
                />
              </section>
            </div>

            <div className='location-area-overlay__toggle-band'>
              <span className='location-area-overlay__toggle-label'>Show:</span>

              {selectedCityName !== 'Denver' && selectedCityName !== 'Nashville' && (
                <label className='location-area-overlay__toggle-item'>
                  <input
                    type='checkbox'
                    checked={showNeighborhoods}
                    onChange={(event) =>
                      setShowNeighborhoods(event.target.checked)
                    }
                  />
                  <span className='location-area-overlay__toggle-switch'></span>
                  <span className='location-area-overlay__toggle-text'>
                    Neighborhoods
                  </span>
                </label>
              )}

              <label className='location-area-overlay__toggle-item'>
                <input
                  type='checkbox'
                  checked={showApartments}
                  onChange={(event) =>
                    setShowApartments(event.target.checked)
                  }
                />
                <span className='location-area-overlay__toggle-switch'></span>
                <span className='location-area-overlay__toggle-text'>
                  Apartment Markers
                </span>
              </label>

              <label className='location-area-overlay__toggle-item'>
                <input
                  type='checkbox'
                  checked={showSchools}
                  onChange={(event) =>
                    setShowSchools(event.target.checked)
                  }
                />
                <span className='location-area-overlay__toggle-switch'></span>
                <span className='location-area-overlay__toggle-text'>
                  Schools
                </span>
              </label>

              <label className='location-area-overlay__toggle-item'>
                <input
                  type='checkbox'
                  checked={showDistricts}
                  onChange={(event) =>
                    setShowDistricts(event.target.checked)
                  }
                />
                <span className='location-area-overlay__toggle-switch'></span>
                <span className='location-area-overlay__toggle-text'>
                  School Districts
                </span>
              </label>

              {hasSpatialSelection && (
                <label className='location-area-overlay__toggle-item'>
                  <input
                    type='checkbox'
                    checked={onlySelectedTop}
                    onChange={(event) =>
                      setOnlySelectedTop(event.target.checked)
                    }
                  />
                  <span className='location-area-overlay__toggle-switch'></span>
                  <span className='location-area-overlay__toggle-text'>
                    Only Selected Areas
                  </span>
                </label>
              )}
            </div>

            <section className='location-area-overlay__summary'>
              <div className='location-area-overlay__summary-row'>
                <div className='location-area-overlay__summary-item'>
                  <span className='location-area-overlay__summary-icon'>📍</span>
                  {selectedCityName || '—'}
                  {selectedNeighborhoods.length > 0 && (
                    <>
                      <span className='location-area-overlay__summary-separator'>•</span>
                      {`${selectedNeighborhoods.length} neighborhood${selectedNeighborhoods.length > 1 ? 's' : ''}`}
                    </>
                  )}
                  {selectedZipCodes.length > 0 && (
                    <>
                      <span className='location-area-overlay__summary-separator'>•</span>
                      {`${selectedZipCodes.length} ZIP code${selectedZipCodes.length > 1 ? 's' : ''}`}
                    </>
                  )}
                </div>
                {showApartments && (
                  <div className='location-area-overlay__summary-item'>
                    <span className='location-area-overlay__summary-icon'>🏢</span>
                    {`${apartmentStats.apartmentsInCity} of ${apartmentStats.totalApartments} apartments`}
                  </div>
                )}
                <div className='location-area-overlay__summary-actions' style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                  <Button color='link' className='px-2' onClick={handleClear}>
                    Clear
                  </Button>
                  <Button color='success' onClick={handleApply}>
                    Next
                  </Button>
                  <button
                    type='button'
                    className='location-area-overlay__summary-close'
                    onClick={handleClose}
                    aria-label='Close location filter'
                  >
                    <span aria-hidden='true'>&times;</span>
                  </button>
                </div>
              </div>
              {(drawSummary.polygons > 0 || (radiusActive && radiusCenter)) && (
                <div className='location-area-overlay__summary-row'>
                  {drawSummary.polygons > 0 && (
                    <div className='location-area-overlay__summary-item'>
                      <span className='location-area-overlay__summary-icon'>✏️</span>
                      {`${drawSummary.polygons} drawn area${drawSummary.polygons > 1 ? 's' : ''}`}
                      {drawSummary.apartments > 0 && (
                        <>
                          <span className='location-area-overlay__summary-separator'>•</span>
                          {`${drawSummary.apartments} apartments`}
                        </>
                      )}
                    </div>
                  )}
                  {radiusActive && radiusCenter && (
                    <div className='location-area-overlay__summary-item'>
                      <span className='location-area-overlay__summary-icon'>⭕</span>
                      {`${radiusSummary.apartments} apartments within ${radiusMiles.toFixed(1)}mi`}
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>

          <div className='location-area-overlay__map'>
            <div className='location-area-overlay__map-shell'>
              <MaptilerMap
                isOpen={isOpen}
                cityName={selectedCityName}
                availableNeighborhoods={neighborhoodOptions}
                selectedNeighborhoods={selectedNeighborhoods}
                onToggleNeighborhood={handleToggleNeighborhood}
                showNeighborhoods={showNeighborhoods}
                showApartments={showApartments}
                showSchools={showSchools}
                showDistricts={showDistricts}
                onlySelectedTop={onlySelectedTop}
                drawMode={drawMode}
                radiusMode={radiusMode}
                radiusActive={radiusActive}
                radiusInMiles={radiusMiles}
                radiusCenter={radiusCenter}
                onDrawUpdate={handleDrawUpdate}
                onRadiusUpdate={handleRadiusUpdate}
                onStatsUpdate={handleStatsUpdate}
                clearDrawTrigger={clearDrawKey}
                selectedZipCodes={selectedZipValues}
                apartmentsData={propertiesData?.propertiesGeojson}
                neighborhoodsData={neighborhoodGeojsonQuery?.data?.neighborhoodsGeojson}
              />
              <div className='location-area-overlay__floating-group' role='group' aria-label='Map tools'>
                <button
                  type='button'
                  className={`location-area-overlay__floating-btn ${drawMode ? 'is-active' : ''
                    }`}
                  onClick={handleDrawButton}
                >
                  <svg
                    width='20'
                    height='20'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                  >
                    <path d='M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7'></path>
                    <path d='M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'></path>
                  </svg>
                  <span>Draw Area</span>
                </button>

                <button
                  type='button'
                  className={`location-area-overlay__floating-btn location-area-overlay__floating-btn--radius ${radiusMode || (radiusActive && radiusCenter) ? 'is-active' : ''
                    }`}
                  onClick={handleRadiusButton}
                >
                  <svg
                    width='20'
                    height='20'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                  >
                    <circle cx='11' cy='11' r='8'></circle>
                    <path d='m21 21-4.35-4.35'></path>
                    <circle cx='11' cy='11' r='1'></circle>
                  </svg>
                  <span>Radius Search</span>
                </button>
              </div>

              {drawPanelOpen && (
                <div className='location-area-overlay__panel location-area-overlay__panel--draw'>
                  <div className='location-area-overlay__panel-header'>
                    <h4>Draw Custom Area</h4>
                    <button
                      type='button'
                      className='location-area-overlay__panel-close'
                      onClick={handleDrawPanelClose}
                      aria-label='Close draw panel'
                    >
                      &times;
                    </button>
                  </div>
                  <div className='location-area-overlay__panel-body'>
                    <div className='location-area-overlay__panel-text'>
                      {drawSummary.text}
                    </div>
                    <div className='location-area-overlay__panel-actions'>
                      <button
                        type='button'
                        className='location-area-overlay__panel-btn btn-secondary'
                        onClick={handleDrawCancel}
                      >
                        ✕ Cancel
                      </button>
                      <button
                        type='button'
                        className='location-area-overlay__panel-btn btn-warning'
                        onClick={handleDrawClearAll}
                        disabled={drawSummary.polygons === 0}
                      >
                        🗑️ Clear All Areas
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {radiusPanelOpen && (
                <div className='location-area-overlay__panel location-area-overlay__panel--radius'>
                  <div className='location-area-overlay__panel-header'>
                    <h4>Radius Search</h4>
                    <button
                      type='button'
                      className='location-area-overlay__panel-close'
                      onClick={handleRadiusClose}
                      aria-label='Close radius panel'
                    >
                      &times;
                    </button>
                  </div>
                  <div className='location-area-overlay__panel-body'>
                    <label className='location-area-overlay__radius-label'>
                      Search Radius:{' '}
                      <span>{radiusMiles.toFixed(1)}</span> miles
                    </label>
                    <input
                      type='range'
                      min='0.2'
                      max='5'
                      step='0.1'
                      value={radiusMiles}
                      className='location-area-overlay__radius-slider'
                      onChange={handleRadiusSliderChange}
                    />
                    <div className='location-area-overlay__panel-text'>
                      {radiusSummary.text}
                    </div>
                    <div className='location-area-overlay__panel-actions'>
                      <button
                        type='button'
                        className='location-area-overlay__panel-btn btn-primary'
                        onClick={handleRadiusApply}
                        disabled={!radiusSummary.ready}
                      >
                        🎯 Apply Filter
                      </button>
                      <button
                        type='button'
                        className='location-area-overlay__panel-btn btn-secondary'
                        onClick={handleRadiusCancel}
                      >
                        ✕ Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationAreaOverlay;
